import { dateFormatter } from 'utils/dateFormatter'
import {
  Response,
  ResponseDiscipline,
} from '../../../../../../RFITrackerContext'
import { List, Tooltip, Typography } from 'antd'
import { alphabeticalSort, Permission } from 'shared'
import { useAuthContext } from 'context/AuthContext'
import { useMemo } from 'react'
import { ByDisciplineItem } from './types'
import ResponseDevButtons from './components/ResponseDevButtons'
import CIMSDivider from 'components/CIMSDivider'
import { errorHandler } from 'hooks/errorHandler'

const Responses: React.VFC<{
  responses: Response[]
  disciplines: ResponseDiscipline[]
}> = ({ responses, disciplines }) => {
  const { user } = useAuthContext()
  const canEdit = user?.authorized(Permission.EditRFIResponse).authorized

  const responsesByDiscipline = useMemo(
    () =>
      responses.reduce(
        (byDisciplineMap, { responseDisciplineId, responseDate, ...rest }) => {
          if (byDisciplineMap.has(responseDisciplineId)) {
            const { responses, ...discipline } =
              byDisciplineMap.get(responseDisciplineId)!
            const newItem: ByDisciplineItem = {
              ...discipline,
              responses: [
                ...responses,
                {
                  ...rest,
                  responseDate: `[${dateFormatter.shortDatetime(
                    responseDate
                  )}]`,
                },
              ],
            }
            return byDisciplineMap.set(responseDisciplineId, newItem)
          }
          const discipline = disciplines.find(
            ({ id }) => id === responseDisciplineId
          )
          if (!discipline)
            errorHandler('Response does not have an associated Discipline')
          const newItem: ByDisciplineItem = {
            ...(discipline as ResponseDiscipline),
            responses: [
              {
                responseDate: `[${dateFormatter.shortDatetime(responseDate)}]`,
                ...rest,
              },
            ],
          }
          return byDisciplineMap.set(responseDisciplineId, newItem)
        },
        new Map<string, ByDisciplineItem>()
      ),
    [responses, disciplines]
  )
  return (
    <List
      style={{ maxWidth: 725 }}
      dataSource={[...responsesByDiscipline.values()].sort(
        alphabeticalSort('name')
      )}
      renderItem={({ name: disciplineName, responses }) => (
        <>
          <CIMSDivider>
            <Typography.Text strong>{disciplineName}</Typography.Text>
          </CIMSDivider>
          {responses.map(
            (
              {
                id,
                text,
                closed,
                user: { displayName, mail },
                responseDate,
                deleted,
              },
              index
            ) => (
              <>
                {(!deleted || canEdit) && (
                  <>
                    <Tooltip title={displayName ? mail : ''}>
                      <Typography
                        style={{
                          display: 'inline-block',
                          marginTop: !canEdit && index > 0 ? 5 : 0,
                        }}
                      >
                        {deleted && (
                          <Typography.Text type="danger">
                            (deleted)
                          </Typography.Text>
                        )}
                        {`${responseDate} ${displayName ?? mail}:`}
                      </Typography>
                    </Tooltip>
                    <Typography>{text}</Typography>
                  </>
                )}
                {canEdit && (
                  <ResponseDevButtons {...{ id, text, closed, deleted }} />
                )}
              </>
            )
          )}
        </>
      )}
    />
  )
}

export default Responses
